/*
  Admin › Access — "Preview as role" (28 #6, wave 3). Pick a role and see the app through its eyes with the
  draft applied: the sidebar it gets, the project tabs it can open (View only marked), what is hidden, what the
  draft changes for it, and who that affects. Nothing is saved from here.
*/
import { forwardRef, useMemo } from 'react';
import {
  Bot, DraftingCompass, Eye, EyeOff, Files, FolderKanban, Info, LayoutDashboard, ListChecks, Package, ReceiptText,
  Settings, ShoppingCart, TrendingUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { SUPER_ADMIN_ROLE } from '@/lib/permissions';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusPill } from '@/components/ui/status-pill';
import { UpperLabel, UserAvatar } from '@/components/admin/AdminUi';
import { rolePreview } from '@/components/admin/adminModel';

const PAGE_ICONS = {
  dashboard: LayoutDashboard,
  tasks: ListChecks,
  projects: FolderKanban,
  shop_drawings: DraftingCompass,
  documents: Files,
  inventory: Package,
  procurement: ShoppingCart,
  forecast: TrendingUp,
  invoicing: ReceiptText,
  ai: Bot,
  admin: Settings,
};

const CHANGE_TEXT = { new: '+ new in draft', removed: 'removed in draft', 'to-view': 'view only in draft', 'to-edit': 'can edit in draft' };
const MAX_FACES = 6;

function ChangeMark({ change, onNavy = false }) {
  if (!change) return null;
  if (onNavy) {
    return (
      <span className={cn('ml-auto shrink-0 rounded-full px-1.5 text-[10.5px] font-bold leading-[18px]', change === 'removed' ? 'bg-white/15 text-white/80 line-through' : 'bg-lime text-navy-950')}>
        {change === 'new' ? '+ new' : change === 'removed' ? 'removed' : change === 'to-view' ? 'view only' : 'can edit'}
      </span>
    );
  }
  return (
    <StatusPill
      tone={change === 'new' || change === 'to-edit' ? 'green' : change === 'removed' ? 'red' : 'amber'}
      label={CHANGE_TEXT[change]}
      size="sm"
      hideDot
      className="shrink-0"
    />
  );
}

const RolePreview = forwardRef(function RolePreview({
  roles = [], role, onRoleChange, savedPermissions = {}, permissions = {}, savedView = {}, viewOnly = {}, users = [], levels = false,
}, ref) {
  const isSuper = role === SUPER_ADMIN_ROLE;
  const preview = useMemo(() => rolePreview({
    role,
    savedPermissions: savedPermissions[role] || [],
    permissions: permissions[role] || [],
    savedView: savedView[role] || [],
    viewOnly: viewOnly[role] || [],
    superAdmin: isSuper,
  }), [role, savedPermissions, permissions, savedView, viewOnly, isSuper]);
  const people = users.filter((user) => user.role === role);
  const hiddenPages = preview.pages.filter((page) => !page.visible);
  const hiddenTabs = preview.tabs.filter((tab) => !tab.visible);
  const shownPages = preview.pages.filter((page) => page.visible || page.change === 'removed');

  return (
    <section
      ref={ref}
      id="access-preview"
      className="scroll-mt-[calc(var(--app-header-height,64px)+16px)] rounded-card border border-line bg-surface shadow-card"
      aria-labelledby="access-preview-title"
      data-testid="access-preview"
    >
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line-2 px-5 py-3.5">
        <h2 id="access-preview-title" className="inline-flex items-center gap-2 text-[15px] font-bold text-ink">
          <Eye className="h-4 w-4 text-ink-2" aria-hidden="true" /> Preview as
        </h2>
        <Select value={role} onValueChange={onRoleChange}>
          <SelectTrigger
            id="access-preview-role"
            className="h-9 w-[220px] rounded-control border-line bg-surface text-[13.5px] font-semibold text-ink"
            aria-label="Role to preview"
            data-testid="access-preview-role"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {roles.map((item) => (
              <SelectItem key={item} value={item} data-testid={`access-preview-role-${item}`}>{item}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[12.5px] text-ink-3" data-testid="access-preview-who">
          {isSuper
            ? 'Super Admin sees everything — always.'
            : `What ${people.length} ${people.length === 1 ? 'person' : 'people'} will see after you apply`}
        </p>
      </header>

      <div className="grid gap-7 px-5 py-4 md:grid-cols-[260px_minmax(0,1fr)]">
        <div className="min-w-0">
          <UpperLabel>Sidebar</UpperLabel>
          {!shownPages.length ? (
            <p className="mt-2 rounded-xl border border-dashed border-line px-3 py-4 text-center text-[12.5px] text-ink-3" data-testid="access-preview-sidebar">
              No pages — this role sees an empty app.
            </p>
          ) : null}
          <ul className={cn('mt-2 rounded-xl bg-navy p-2.5 text-white', !shownPages.length && 'hidden')} aria-label={`Sidebar of ${role}`} data-testid={shownPages.length ? 'access-preview-sidebar' : undefined}>
            {shownPages.map((page, index) => {
              const Icon = PAGE_ICONS[page.key] || LayoutDashboard;
              return (
                <li
                  key={page.key}
                  className={cn(
                    'flex h-[30px] items-center gap-2.5 rounded-lg px-2.5 text-[12.5px]',
                    index === 0 && page.visible && 'bg-white/10 font-semibold',
                    page.change === 'removed' && 'text-white/55'
                  )}
                  data-testid={`access-preview-page-${page.key}`}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden="true" />
                  <span className={cn('min-w-0 truncate', page.change === 'removed' && 'line-through')}>{page.label}</span>
                  {levels && page.visible && page.level === 'view' && !page.change ? (
                    <Eye className="ml-auto h-3.5 w-3.5 shrink-0 opacity-70" aria-label="view only" />
                  ) : null}
                  <ChangeMark change={page.change} onNavy />
                </li>
              );
            })}
          </ul>
          <p className="mt-2 flex items-start gap-1.5 text-[12px] text-ink-3" data-testid="access-preview-hidden-pages">
            <EyeOff className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {hiddenPages.length ? `Hidden: ${hiddenPages.map((page) => page.label).join(' · ')}` : 'Nothing hidden'}
          </p>
        </div>

        <div className="min-w-0 space-y-4">
          <div>
            <UpperLabel>Inside a project</UpperLabel>
            <ul className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-line pb-2" aria-label={`Project tabs of ${role}`} data-testid="access-preview-tabs">
              {preview.tabs.filter((tab) => tab.visible || tab.change === 'removed').map((tab, index) => (
                <li
                  key={tab.key}
                  className={cn(
                    'inline-flex items-center gap-1.5 text-[13px]',
                    index === 0 ? 'font-semibold text-ink' : 'text-ink-2',
                    tab.change === 'removed' && 'text-ink-3 line-through'
                  )}
                  data-testid={`access-preview-tab-${tab.key}`}
                >
                  {tab.label}
                  {levels && tab.visible && tab.level === 'view' ? (
                    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-ink-3"><Eye className="h-3 w-3" aria-hidden="true" />view</span>
                  ) : null}
                  <ChangeMark change={tab.change} />
                </li>
              ))}
            </ul>
            <p className="mt-2 flex items-start gap-1.5 text-[12px] text-ink-3" data-testid="access-preview-hidden-tabs">
              <EyeOff className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {hiddenTabs.length ? `Hidden: ${hiddenTabs.map((tab) => `${tab.label} tab`).join(' · ')}` : 'Every project tab'}
            </p>
          </div>

          <div className="flex items-start gap-2.5 rounded-xl border border-brand-soft-border bg-brand-soft px-3.5 py-2.5 text-[13px] text-ink" role="status" data-testid="access-preview-summary">
            <Info className="mt-0.5 h-[15px] w-[15px] shrink-0 text-brand-ink" aria-hidden="true" />
            <p>{preview.summary}</p>
          </div>

          <div>
            <UpperLabel>Who this affects</UpperLabel>
            {people.length ? (
              <div className="mt-2 flex flex-wrap items-center gap-3" data-testid="access-preview-people">
                <span className="flex -space-x-1" aria-hidden="true">
                  {people.slice(0, MAX_FACES).map((person) => (
                    <UserAvatar key={person.id} name={person.name} seed={person.id} size={28} className="ring-2 ring-[color:var(--surface)]" />
                  ))}
                  {people.length > MAX_FACES ? (
                    <span className="inline-grid h-7 w-7 place-items-center rounded-full bg-surface-2 text-[11px] font-bold text-ink-2 ring-2 ring-[color:var(--surface)]">
                      +{people.length - MAX_FACES}
                    </span>
                  ) : null}
                </span>
                <span className="min-w-0 text-[13px] text-ink-2">
                  {people.length <= MAX_FACES + 1
                    ? people.map((person) => person.name).join(', ')
                    : `${people.slice(0, MAX_FACES).map((person) => person.name).join(', ')} and ${people.length - MAX_FACES} more`}
                </span>
              </div>
            ) : (
              <p className="mt-2 text-[13px] text-ink-3" data-testid="access-preview-people">Nobody has this role yet.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
});

export default RolePreview;
